/**
 * Application Router
 * Defines all routes and their components
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { lazy, Suspense } from 'react';

// Layout Components
import AuthLayout from '@/layouts/AuthLayout';
import ProtectedLayout from '@/layouts/ProtectedLayout';
import Loading from '@/components/common/Loading';

// Page Components (lazy loaded)
const Map = lazy(() => import('@/pages/Map/Map'));
const Jobs = lazy(() => import('@/pages/Jobs/Jobs'));
const JobDetail = lazy(() => import('@/pages/Jobs/JobDetail/JobDetail'));
const EtlPipeline = lazy(() => import('@/pages/EtlPipeline'));
const QueueMonitor = lazy(() => import('@/pages/QueueMonitor/QueueMonitor'));
const Config = lazy(() => import('@/pages/Config/Config'));
const ApiKeys = lazy(() => import('@/pages/ApiKeys/ApiKeys'));
const BatchJobRunner = lazy(() => import('@/pages/BatchJobRunner/BatchJobRunner'));
const DataPlatform = lazy(() => import('@/pages/DataPlatform/DataPlatform'));
const BatchJobs = lazy(() => import('@/pages/BatchJobs/BatchJobs'));
const Instagram = lazy(() => import('@/pages/Instagram/Instagram'));
const NewsSignals = lazy(() => import('@/pages/NewsSignals/NewsSignals'));
const SocialYTTT = lazy(() => import('@/pages/SocialYTTT/SocialYTTT'));
const Respider = lazy(() => import('@/pages/Respider/Respider'));
const PipelineStatus = lazy(() => import('@/pages/PipelineStatus/PipelineStatus'));
const GoogleMapsSpider = lazy(() => import('@/pages/GoogleMapsSpider/GoogleMapsSpider'));
const EntityResolution = lazy(() => import('@/pages/EntityResolution/EntityResolution'));
const MenuOCR = lazy(() => import('@/pages/MenuOCR/MenuOCR'));
const SerperEnricher = lazy(() => import('@/pages/SerperEnricher/SerperEnricher'));
const OutletDetail = lazy(() => import('@/pages/OutletDetail/OutletDetail'));
const OutletLookup = lazy(() => import('@/pages/OutletLookup/OutletLookup'));
const Login = lazy(() => import('@/pages/Auth/Login'));
const Register = lazy(() => import('@/pages/Auth/Register'));

// Home route component (redirects based on auth state)
function Home() {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  if (user) {
    return <Navigate to="/map" replace />;
  }
  return <Navigate to="/login" replace />;
}

export default function AppRouter() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Home />} />

        {/* Auth Routes */}
        <Route
          path="/login"
          element={
            <AuthLayout>
              <Login />
            </AuthLayout>
          }
        />
        <Route
          path="/register"
          element={
            <AuthLayout>
              <Register />
            </AuthLayout>
          }
        />

        {/* Protected Routes */}
        <Route element={<ProtectedLayout />}>
          <Route path="/map" element={<Map />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/jobs/:jobId" element={<JobDetail />} />
          <Route path="/etl" element={<EtlPipeline />} />
          <Route path="/queue-monitor" element={<QueueMonitor />} />
          <Route path="/batch/new" element={<BatchJobRunner />} />
          <Route path="/batch/jobs" element={<BatchJobs />} />
          <Route path="/api-keys" element={<ApiKeys />} />
          <Route path="/instagram" element={<Instagram />} />
          <Route path="/news-signals" element={<NewsSignals />} />
          <Route path="/social-yttt" element={<SocialYTTT />} />
          <Route path="/respider" element={<Respider />} />
          <Route path="/pipeline-status" element={<PipelineStatus />} />
          <Route path="/gmaps-reviews" element={<GoogleMapsSpider />} />
          <Route path="/entity-resolution" element={<EntityResolution />} />
          <Route path="/menu-ocr" element={<MenuOCR />} />
          <Route path="/serper-enricher" element={<SerperEnricher />} />
          <Route path="/outlets" element={<OutletLookup />} />
          <Route path="/outlets/:placeId" element={<OutletDetail />} />
          <Route path="/config" element={<Config />} />
          <Route path="/data-platform" element={<DataPlatform />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
